import { useQuery } from '@apollo/client/react';
import { GROUP_SHAREDS_QUERY } from '@scio3/graphql-client';
import type { AssetGroup, GroupSort, GroupType } from '@scio3/shared-types';
import { useTeamStore } from '@/stores/team-store';

export interface GroupSharedsResult {
  groupShareds: {
    page: {
      edges: Array<{ node: AssetGroup }>;
    };
  };
}

/**
 * Fetches folders shared with the current user via folder-level security.
 * Uses the dedicated `groupShareds` resolver which filters groups by
 * securities/securityTeams matching the authenticated user.
 *
 * All 4 features (screens, assets, playlists, schedules) reuse this hook
 * when in "Shared with me" mode. The returned folders are the shared root
 * view — clicking a folder navigates into it with the real path.
 *
 * @param type - GroupType value from GROUP_TYPES enum (lowercase). Automatically
 *   converted to UPPERCASE for GraphQL enum convention.
 */
export function useSharedFolders(type: GroupType, options?: { skip?: boolean; sort?: GroupSort }) {
  const { activeTeamId } = useTeamStore();
  const skip = options?.skip ?? false;

  // GraphQL enums require UPPERCASE values (NestJS registerEnumType convention)
  const graphqlType = type.toUpperCase();

  const { data, loading, refetch } = useQuery<GroupSharedsResult>(GROUP_SHAREDS_QUERY, {
    variables: {
      teamId: activeTeamId,
      first: 200,
      query: { type: graphqlType },
      sort: options?.sort ?? { name: 'ASC' },
    },
    skip: !activeTeamId || skip,
    fetchPolicy: 'cache-and-network',
  });

  const rawFolders: AssetGroup[] = data?.groupShareds?.page?.edges?.map((e) => e.node) ?? [];
  const sharedFolders = filterTopLevelFolders(rawFolders);

  return { sharedFolders, loading, refetch };
}

/**
 * Filters shared folders to show only the topmost in each hierarchy.
 * If /Marketing and /Marketing/Campaigns are both shared, only /Marketing is shown.
 * This matches scio1's formatGroupShareWithMe() behavior.
 * Scoped per team so folders from different teams with the same path both appear.
 */
export function filterTopLevelFolders(folders: AssetGroup[]): AssetGroup[] {
  const folderPaths = folders.map((f) => ({
    folder: f,
    fullPath: ((f.path ?? '') + '/' + (f.name ?? '')).toLowerCase(),
    teamId: f.teamId ?? '',
  }));

  return folderPaths
    .filter(({ fullPath, teamId }) => {
      // Keep if no other folder from the SAME team is an ancestor
      return !folderPaths.some(
        (other) =>
          other.teamId === teamId &&
          other.fullPath !== fullPath &&
          fullPath.startsWith(other.fullPath + '/'),
      );
    })
    .map(({ folder }) => folder);
}
